import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import confetti from 'canvas-confetti';

const BRAND_COLORS = ['#FFE100', '#3b82f6', '#22d3ee', '#c084fc', '#60a5fa'];

const fireConfetti = () => {
  confetti({
    particleCount: 60,
    spread: 70,
    origin: { y: 0.6, x: 0.5 },
    colors: BRAND_COLORS,
    gravity: 1.2,
    scalar: 0.9,
    ticks: 150,
    disableForReducedMotion: true,
  });

  setTimeout(() => {
    confetti({
      particleCount: 35,
      spread: 100,
      origin: { y: 0.55, x: 0.5 },
      colors: BRAND_COLORS,
      gravity: 1.4,
      scalar: 0.7,
      ticks: 120,
      disableForReducedMotion: true,
    });
  }, 150);
};

const BrandLogo = () => (
  <div className="relative mx-auto w-20 h-20 animate-scale-up">
    {/* Ambient glow */}
    <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 opacity-20 dark:opacity-30 blur-xl" />

    {/* Logo with gradient border */}
    <div className="relative w-20 h-20 rounded-2xl p-[2px] bg-gradient-to-br from-blue-400 via-cyan-400 to-purple-400 shadow-lg shadow-cyan-500/20">
      <div className="w-full h-full bg-white dark:bg-gray-900 rounded-[14px] p-2.5 flex items-center justify-center">
        <img src="/logo-128.png" alt="Ultimate Web Scraper" className="w-full h-full" />
      </div>
    </div>

    {/* Animated checkmark badge */}
    <div className="absolute -bottom-1.5 -right-1.5 w-8 h-8 rounded-full bg-white dark:bg-gray-900 shadow-lg flex items-center justify-center ring-2 ring-white dark:ring-gray-900">
      <svg viewBox="0 0 36 36" className="w-full h-full">
        <style>{`
          @keyframes circleDraw {
            from { stroke-dashoffset: 88; }
            to { stroke-dashoffset: 0; }
          }
          @keyframes checkDraw {
            from { stroke-dashoffset: 24; }
            to { stroke-dashoffset: 0; }
          }
        `}</style>
        <circle
          cx="18" cy="18" r="14"
          fill="none"
          stroke="url(#checkGradient)"
          strokeWidth="2.5"
          strokeLinecap="round"
          style={{
            strokeDasharray: 88,
            strokeDashoffset: 88,
            animation: 'circleDraw 0.5s ease-out 0.4s forwards',
          }}
        />
        <path
          d="M11 19l4 4 9-9"
          fill="none"
          stroke="url(#checkGradient)"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            strokeDasharray: 24,
            strokeDashoffset: 24,
            animation: 'checkDraw 0.35s ease-out 0.85s forwards',
          }}
        />
        <defs>
          <linearGradient id="checkGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#3b82f6" />
            <stop offset="50%" stopColor="#22d3ee" />
            <stop offset="100%" stopColor="#c084fc" />
          </linearGradient>
        </defs>
      </svg>
    </div>
  </div>
);

const CheckoutSuccessModal = ({ type, creditAmount, source, onClose }) => {
  const [show, setShow] = useState(false);
  const confettiFired = useRef(false);
  const navigate = useNavigate();

  const isCredits = type === 'credits';
  // `source === 'consent'`: the purchase started from the OAuth consent page's
  // upgrade detour (see OAuthConsent.jsx). The consent tab is still open and
  // unlocks on focus, so the success copy points back there instead of sending
  // the user to the dashboard.
  const isConsentFlow = source === 'consent';

  // Subscription success → send the user into the app. Credits → stay put.
  // Consent flow → stay put too; the next step is the tab they left open.
  const handleDismiss = () => {
    onClose();
    if (!isCredits && !isConsentFlow) navigate('/dashboard');
  };

  useEffect(() => {
    requestAnimationFrame(() => setShow(true));
    if (!confettiFired.current) {
      confettiFired.current = true;
      setTimeout(fireConfetti, 200);
    }
  }, []);

  useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape') handleDismiss(); };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose, isCredits]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleDismiss}
      />

      <div className={`relative bg-white dark:bg-gray-900/95 rounded-2xl border border-gray-200/80 dark:border-gray-700/50 shadow-2xl shadow-cyan-500/10 dark:shadow-cyan-500/5 w-full max-w-sm mx-4 overflow-hidden transition-all duration-500 ease-out ${show ? 'animate-scale-up' : 'opacity-0 scale-95'}`}>
        <div className="h-1.5 bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500" />

        <div className="px-8 pt-10 pb-4 text-center">
          <BrandLogo />

          {isCredits ? (
            <div className="mt-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Credits Added!
              </h2>
              {creditAmount && (
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-blue-500/10 via-cyan-500/10 to-purple-500/10 dark:from-blue-500/15 dark:via-cyan-500/15 dark:to-purple-500/15 border border-cyan-400/20 dark:border-cyan-400/15">
                  <span className="text-lg font-bold bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 bg-clip-text text-transparent">
                    +{Number(creditAmount).toLocaleString()}
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400 font-medium">credits</span>
                </div>
              )}
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-3">
                Your credits are ready to use right away.
              </p>
            </div>
          ) : (
            <div className="mt-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                You're all set!
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                Your subscription is now active.
              </p>

              {isConsentFlow ? (
                /* The purchase started from an OAuth consent tab — close the loop. */
                <div className="mt-6 text-left rounded-xl border border-gray-200 dark:border-gray-800 bg-white/50 dark:bg-gray-900/40 px-5 py-4">
                  <p className="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2.5">
                    One step left — finish connecting
                  </p>
                  <ol className="space-y-2">
                    <li className="flex gap-2.5 text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                      <span className="flex-shrink-0 text-gray-400 dark:text-gray-500 font-medium tabular-nums">1.</span>
                      <span>
                        Go back to the tab you left open —{' '}
                        <span className="font-medium text-gray-900 dark:text-white">it unlocks the moment you switch back</span>.
                      </span>
                    </li>
                    <li className="flex gap-2.5 text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                      <span className="flex-shrink-0 text-gray-400 dark:text-gray-500 font-medium tabular-nums">2.</span>
                      <span>
                        Click <span className="font-medium text-gray-900 dark:text-white">Allow access</span> — your AI
                        app connects in seconds.
                      </span>
                    </li>
                  </ol>
                </div>
              ) : (
                /* How to activate Extension PRO */
                <div className="mt-6 text-left rounded-xl border border-gray-200 dark:border-gray-800 bg-white/50 dark:bg-gray-900/40 px-5 py-4">
                  <p className="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2.5">
                    How to activate Extension PRO
                  </p>
                  <ol className="space-y-1.5">
                    <li className="flex gap-2.5 text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                      <span className="flex-shrink-0 text-gray-400 dark:text-gray-500 font-medium tabular-nums">1.</span>
                      <span>
                        Open the extension →{' '}
                        <span className="font-medium text-gray-900 dark:text-white">Settings</span>
                        {' '}<span aria-hidden="true">⚙️</span>
                      </span>
                    </li>
                    <li className="flex gap-2.5 text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                      <span className="flex-shrink-0 text-gray-400 dark:text-gray-500 font-medium tabular-nums">2.</span>
                      <span>Sign in to your account</span>
                    </li>
                  </ol>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-8 pb-8 pt-2">
          <button
            onClick={handleDismiss}
            className="w-full py-3 rounded-xl font-bold text-sm bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 text-white hover:from-blue-600 hover:via-cyan-600 hover:to-purple-600 transition-all duration-200 shadow-md shadow-cyan-500/20 hover:shadow-lg hover:shadow-cyan-500/30 hover:scale-[1.02] transform"
          >
            {isCredits ? 'Continue' : isConsentFlow ? 'Done' : 'Go to Dashboard'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CheckoutSuccessModal;
